import { useState, useCallback, useRef, useEffect } from 'react';
import {
  BarChart3,
  Bot,
  CheckCircle2,
  FileText,
  Info,
  List,
  RotateCcw,
  Send,
  Sparkles,
  AlertTriangle,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../services/api';
import { QueryResponse } from '../types';
import ChatFeedback from '../components/ChatFeedback';

interface Turn {
  question: string;
  response?: QueryResponse;
  error?: string;
}

export default function QueryPage() {
  const [question, setQuestion] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [turns, loading]);

  const handleClearHistory = useCallback(async () => {
    if (loading || clearing) return;
    setClearing(true);
    try {
      await api.query.clearHistory();
      setTurns([]);
    } catch {
      // silently ignore — local state is cleared regardless
      setTurns([]);
    } finally {
      setClearing(false);
    }
  }, [loading, clearing]);

  const SUGGESTIONS = [
    {
      icon: BarChart3,
      label: 'Signals by agency',
      query: 'How many signals do we have grouped by agency?',
    },
    {
      icon: AlertTriangle,
      label: 'Critical & high urgency',
      query:
        'List the most recent critical and high urgency signals with their titles and agencies.',
    },
    {
      icon: List,
      label: 'Latest 10 signals',
      query: 'Show me the 10 most recently published regulatory signals.',
    },
  ] as const;

  const handleSuggestion = useCallback(
    (q: string) => {
      if (loading) return;
      setQuestion('');
      const turnIndex = turns.length;
      setTurns((prev) => [...prev, { question: q }]);
      setLoading(true);
      api.query
        .ask(q)
        .then((result) =>
          setTurns((prev) =>
            prev.map((t, i) => (i === turnIndex ? { ...t, response: result.data } : t))
          )
        )
        .catch((err: unknown) => {
          const message =
            err instanceof Error
              ? err.message
              : (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
                'Failed to process query';
          setTurns((prev) => prev.map((t, i) => (i === turnIndex ? { ...t, error: message } : t)));
        })
        .finally(() => setLoading(false));
    },
    [loading, turns.length]
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const trimmedQuestion = question.trim();
      if (!trimmedQuestion || loading) return;

      const turnIndex = turns.length;
      setTurns((prev) => [...prev, { question: trimmedQuestion }]);
      setQuestion('');
      setLoading(true);

      try {
        const result = await api.query.ask(trimmedQuestion);
        setTurns((prev) =>
          prev.map((t, i) => (i === turnIndex ? { ...t, response: result.data } : t))
        );
      } catch (err: unknown) {
        const message =
          err instanceof Error
            ? err.message
            : (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
              'Failed to process query';
        setTurns((prev) => prev.map((t, i) => (i === turnIndex ? { ...t, error: message } : t)));
      } finally {
        setLoading(false);
      }
    },
    [question, loading, turns.length]
  );

  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-900 font-sans">
      <main className="mx-auto flex min-h-screen max-w-4xl flex-col px-4 pb-8 pt-6 sm:px-6 lg:px-8">
        <header className="flex items-center justify-between border-b border-slate-200 pb-4">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-700 text-white shadow-[0_0_14px_rgba(37,99,235,0.2)]">
              <Sparkles className="h-4 w-4" />
            </div>
            <div>
              <h1 className="text-sm font-bold text-slate-900">Analyzer</h1>
              <p className="text-[11px] text-slate-500">Regulatory intelligence assistant</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {turns.length > 0 && (
              <button
                onClick={handleClearHistory}
                disabled={loading || clearing}
                title="Start a new conversation"
                className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-medium text-slate-600 shadow-sm transition hover:border-slate-300 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <RotateCcw className={`h-3 w-3 ${clearing ? 'animate-spin' : ''}`} />
                New conversation
              </button>
            )}
            <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[10px] font-semibold text-emerald-700">
              Live route
            </span>
          </div>
        </header>

        <div className="mt-4 flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 text-xs text-amber-950">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
          <p>Ask questions across the connected regulatory signal knowledge base.</p>
        </div>

        <section className="flex flex-1 flex-col py-8">
          {turns.length === 0 && !loading ? (
            <div className="m-auto w-full max-w-2xl py-12 text-center">
              <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700">
                <Bot className="h-6 w-6" />
              </div>
              <h2 className="font-display text-3xl font-semibold tracking-tight text-slate-900">
                What would you like to investigate?
              </h2>
              <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-slate-500">
                Ask about regulatory signals, affected products, or reporting requirements.
              </p>
              <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
                {SUGGESTIONS.map(({ icon: Icon, label, query }) => (
                  <button
                    key={label}
                    onClick={() => handleSuggestion(query)}
                    className="group flex flex-col items-start gap-2 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-blue-300 hover:shadow-md active:scale-[0.98]"
                  >
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 text-blue-700 transition group-hover:bg-blue-100">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="text-xs font-semibold text-slate-700">{label}</span>
                    <span className="text-[11px] leading-relaxed text-slate-400">{query}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="mx-auto w-full max-w-3xl space-y-8">
              {turns.map((turn, idx) => (
                <div key={idx} className="space-y-4">
                  {/* User message */}
                  <div className="flex justify-end">
                    <div className="max-w-[85%] rounded-2xl rounded-br-md bg-blue-700 px-4 py-3 text-sm leading-relaxed text-white shadow-sm">
                      {turn.question}
                    </div>
                  </div>

                  {/* Bot answer */}
                  {turn.response && (
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-900 text-white">
                          <Bot className="h-4 w-4" />
                        </div>
                        <div className="prose prose-sm prose-slate min-w-0 max-w-none flex-1 pt-1 leading-7 [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-slate-200 [&_td]:px-3 [&_td]:py-2 [&_td]:text-xs [&_th]:border [&_th]:border-slate-200 [&_th]:bg-slate-50 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:text-xs [&_th]:font-semibold">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {turn.response.answer || 'No answer returned'}
                          </ReactMarkdown>
                        </div>
                      </div>

                      {turn.response.sources && turn.response.sources.length > 0 && (
                        <div className="ml-11 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                          <div className="mb-3 flex items-center gap-2 text-xs font-semibold text-slate-800">
                            <FileText className="h-3.5 w-3.5 text-blue-700" />
                            Sources
                          </div>
                          <ul className="space-y-2">
                            {turn.response.sources.map((src, i) => (
                              <li
                                key={i}
                                className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-2 text-xs"
                              >
                                <span className="min-w-0 truncate font-medium text-slate-700">
                                  {typeof src === 'string'
                                    ? src
                                    : (src as { title?: string }).title || 'Document citation'}
                                </span>
                                <span className="flex shrink-0 items-center gap-1 text-[10px] font-semibold text-emerald-700">
                                  <CheckCircle2 className="h-3 w-3" />
                                  Verified
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {turn.response.traceId && (
                        <div className="ml-11 border-t border-slate-200 pt-3">
                          <ChatFeedback traceId={turn.response.traceId} />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Error */}
                  {turn.error && (
                    <div className="ml-11 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-xs font-medium text-rose-950">
                      {turn.error}
                    </div>
                  )}
                </div>
              ))}

              {/* Typing indicator for the in-flight request */}
              {loading && (
                <div className="flex items-start gap-3">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-900 text-white">
                    <Bot className="h-4 w-4" />
                  </div>
                  <div className="rounded-2xl rounded-tl-md border border-slate-200 bg-white px-4 py-3 shadow-sm">
                    <div className="flex gap-1" aria-label="Analyzer is responding">
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-400" />
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-400 [animation-delay:150ms]" />
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-slate-400 [animation-delay:300ms]" />
                    </div>
                  </div>
                </div>
              )}

              <div ref={bottomRef} />
            </div>
          )}
        </section>

        <form onSubmit={handleSubmit} className="mx-auto w-full max-w-3xl">
          <div className="flex items-end gap-2 rounded-2xl border border-slate-300 bg-white p-2 shadow-[0_4px_18px_rgba(15,23,42,0.06)] focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100">
            <textarea
              id="question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (!loading && question.trim()) {
                    handleSubmit(e as unknown as React.FormEvent);
                  }
                }
              }}
              placeholder="Ask about regulatory intelligence... (Enter to send, Shift+Enter for new line)"
              rows={1}
              className="max-h-32 min-h-10 flex-1 resize-none bg-transparent px-2.5 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400"
              aria-label="Ask the Analyzer"
            />
            <button
              type="submit"
              disabled={loading || !question.trim()}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-700 text-white transition hover:bg-blue-800 active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
              aria-label="Send message"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-2 text-center text-[10px] text-slate-400">
            Analyzer responses may be incomplete.
          </p>
        </form>
      </main>
    </div>
  );
}
